import React, { useState } from "react";
import { authApi, invitesApi } from "../../../services/api";
import {
  AuthResponseDto,
  InitialLandingPageProps,
} from "../../../common/types";
import { setStorage } from "../../../common/storageHelper";
import {
  customerInfoIdValue,
  tokenValue,
  userNameValue,
} from "../../../common/helperConstants";
import {
  getInviteId,
  PageFooter,
  PageHeader,
} from "../../../common/helperMethods";
import OtpInput from "./OtpInput";
import CircleSpinner from "../../../common/loader/circleSpinner";
import {
  InvalidInvitationLinkText,
  InvalidVerificationCodeText,
  SomethingWentWrongText,
  TooManyRequestsText,
  VerificationCodeSentText,
  VerificationFailedText,
} from "../../../common/messageContainer";
import { toast } from "react-toastify";

const InitialLandingPage: React.FC<InitialLandingPageProps> = ({
  onGoToChooseLoan,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoader, setIsLoader] = useState<boolean>(false);

  const isOtpComplete = otp.every((digit) => digit.match(/^[0-9]$/));

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isOtpComplete) {
      const currentInviteId = getInviteId();
      if (!currentInviteId) {
        toast.error(InvalidInvitationLinkText);
        return;
      }

      try {
        setIsLoader(true);
        const reqOtp = otp.join("");

        const response = await authApi.verifyOtp(
          currentInviteId,
          Number(reqOtp),
        );

        if (response?.success) {
          const authRes: AuthResponseDto = response;
          setStorage(tokenValue, authRes.token);
          setStorage(customerInfoIdValue, authRes.customerInfoId);
          setStorage(userNameValue, authRes.customerName);
          onGoToChooseLoan();
        } else {
          toast.error(InvalidVerificationCodeText);
        }
      } catch (error: any) {
        if (error.response?.status === 429) {
          toast.error(error?.response?.data);
        } else {
          toast.error(error.response?.data?.error || VerificationFailedText);
        }
      } finally {
        setIsLoader(false);
      }
    }
  };

  const handleResend = async () => {
    const currentInviteId = getInviteId();

    if (!currentInviteId) {
      toast.error(InvalidInvitationLinkText);
      return;
    }

    try {
      setIsLoader(true);

      const response = await invitesApi.resendOtp(currentInviteId);
      if (response?.success) {
        toast.success(VerificationCodeSentText);
        setOtp(["", "", "", "", "", ""]);
      } else {
        toast.error(SomethingWentWrongText);
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        toast.error(error?.response?.data);
      } else {
        toast.error(error.response?.data?.error || SomethingWentWrongText);
      }
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-white md:bg-transparent">
      <div className={`${isLoader === true ? "loaderGrey" : "loaderDisplay"}`}>
        <CircleSpinner />
      </div>
      <PageHeader />

      <main className="flex-1 flex flex-col xl:flex-row overflow-y-auto">
        {/* Left Column: Form Area */}
        <div className="w-full xl:w-1/2 bg-white flex flex-col justify-center items-center p-6 sm:p-10 lg:p-16">
          <div className="w-full max-w-md text-center">
            <h1 className="text-xl sm:text-2xl font-normal text-gray-700 mb-4">
              Enter the 6-digit verification code that was sent to your email
              address.
            </h1>
            <form onSubmit={handleVerify}>
              <div className="space-y-8 mt-8">
                <OtpInput otp={otp} onChange={setOtp} />
                <button
                  type="submit"
                  disabled={!isOtpComplete}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 md:py-4 rounded-lg text-lg transition-all duration-150 ease-in-out disabled:bg-blue-300 disabled:cursor-not-allowed"
                  aria-label="Verify Account"
                >
                  Verify Account
                </button>
              </div>
            </form>
            <p className="text-sm text-gray-500 mt-6">
              Didn't receive a code?{" "}
              <button
                onClick={handleResend}
                disabled={isLoader}
                className="font-medium text-blue-600 hover:underline disabled:text-blue-300 disabled:cursor-not-allowed"
              >
                {"Resend"}
              </button>
            </p>
          </div>
        </div>

        {/* Image Area: Visible on all screens, below form on mobile */}
        <div className="w-full flex-1 bg-transparent p-6 xl:flex-none xl:p-0 xl:w-1/2 xl:relative">
          <img
            src="media/images/Veros-Credit-Family.jpg"
            alt="Happy family using a tablet"
            className="rounded-lg xl:rounded-none w-full h-full object-contain xl:absolute xl:inset-0 xl:w-full xl:h-full xl:object-cover"
          />
        </div>
      </main>
      <PageFooter />
    </div>
  );
};

export default InitialLandingPage;
